import { ComponentProps, FC, SVGProps } from 'react';

import { motion, MotionValue, useTransform } from 'framer-motion';

const transition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 8,
};
const variants = {
  show: (custom: number) => ({
    x: custom ** 3 * 10,
    transition,
  }),
  hide: () => ({
    x: 0,
    transition,
  }),
};

const Group: FC<
  {
    custom: -2 | -1 | 1 | 2;
    x: MotionValue<number>;
  } & Omit<SVGProps<SVGElement>, 'x'>
> = ({ custom, x, children, ...props }) => (
  <motion.g {...(props as any)} custom={custom} variants={variants}>
    <motion.g style={{ x }}>{children}</motion.g>
  </motion.g>
);
const Svg: FC<SVGProps<SVGElement>> = (props) => (
  <motion.svg
    {...(props as any)}
    width='100%'
    height='100%'
    preserveAspectRatio='xMidYMid slice'
    initial='hide'
    whileInView='show'
    viewport={{ once: true }}
  />
);

const useAnimator = (motionValue: MotionValue) => {
  const scale = useTransform(motionValue, [0, 1], [0, 100]);
  const left2 = useTransform(scale, (x) => x * -2);
  const left1 = useTransform(scale, (x) => x * -1);
  const right1 = scale;
  const right2 = useTransform(scale, (x) => x * 2);

  const customMap = {
    '-2': left2,
    '-1': left1,
    '1': right1,
    '2': right2,
  };

  return {
    variants,
    scale,
    left1,
    left2,
    right1,
    right2,
    Svg,
    Group: (props: Omit<ComponentProps<typeof Group>, 'x'>) => (
      // eslint-disable-next-line react/destructuring-assignment
      <Group {...props} x={customMap[`${props.custom}`]} />
    ),
  };
};

export default useAnimator;
